import { targetDisplayEl, scoreListEl } from './dom.js';
import { model, net, ui } from '../engine/state.js';
import { serverNowMs } from '../engine/time.js';
import { setTextIfChanged } from '../utils/utils.js';
import { playerColorById, playerLabelById } from '../engine/players.js';
import { TEAM_FIRE, TEAM_WATER, normalizeTeam, teamIconClass, teamLabel } from '/shared/teams.js';
import { remainingSeconds } from '../engine/state-helpers.js';
import { DEFAULT_CONFIG } from '../engine/config.js';

const MATCH_TIMER_URGENCY_SECONDS = 20;

function ensureScoreRow(playerId) {
  const id = Number(playerId);
  const existing = ui.scoreRows.get(id);
  if (existing) return existing;

  const row = document.createElement('li');
  row.className = 'score-entry';
  row.dataset.playerId = String(id);

  const left = document.createElement('div');
  left.className = 'entry-left';

  const dot = document.createElement('span');
  dot.className = 'dot';

  const roundDelta = document.createElement('span');
  roundDelta.className = 'score-round-delta';
  roundDelta.hidden = true;

  const label = document.createElement('span');
  label.className = 'entry-label';

  const lag = document.createElement('span');
  lag.className = 'score-lag-indicator';
  lag.hidden = true;
  const lagIcon = document.createElement('i');
  lagIcon.className = 'bi bi-reception-1';
  lagIcon.setAttribute('aria-hidden', 'true');
  const lagText = document.createElement('span');
  lagText.textContent = 'LAG';
  lag.append(lagIcon, lagText);

  const score = document.createElement('div');
  score.className = 'score-num';

  left.appendChild(dot);
  left.appendChild(roundDelta);
  left.appendChild(label);
  left.appendChild(lag);
  row.appendChild(left);
  row.appendChild(score);

  const refs = {
    row,
    dot,
    roundDelta,
    label,
    lag,
    score
  };
  ui.scoreRows.set(id, refs);
  return refs;
}

function formatClockSeconds(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

function resetTargetCountdown() {
  ui.targetCountdownEndsAt = 0;
  ui.targetCountdownSeconds = -1;
}

function targetCountdownSeconds(roundEndsAt, nowMs, options = {}) {
  const secsLeft = remainingSeconds(roundEndsAt, nowMs);
  if (options.monotonic === false) {
    ui.targetCountdownEndsAt = roundEndsAt;
    ui.targetCountdownSeconds = secsLeft;
    return secsLeft;
  }
  if (ui.targetCountdownEndsAt !== roundEndsAt || ui.targetCountdownSeconds < 0) {
    ui.targetCountdownEndsAt = roundEndsAt;
    ui.targetCountdownSeconds = secsLeft;
    return secsLeft;
  }
  ui.targetCountdownSeconds = Math.min(ui.targetCountdownSeconds, secsLeft);
  return ui.targetCountdownSeconds;
}

function renderTargetDisplay(nowMs = serverNowMs(), options = {}) {
  if (targetDisplayEl) {
    const status = model.state?.status || {};
    const roundEndsAt = Number(status.roundEndsAt);
    const freezeUntilMs = Number(status.roundStartFreezeUntilMs) || 0;
    const roundDurationMs = Number(model.config?.gemRoundDurationMs) || DEFAULT_CONFIG.gemRoundDurationMs;
    let urgent = false;
    if (Number.isFinite(roundEndsAt) && roundEndsAt > 0 && !status.gameOver) {
      if (freezeUntilMs > nowMs && Number.isFinite(roundDurationMs) && roundDurationMs > 0) {
        resetTargetCountdown();
        setTextIfChanged(targetDisplayEl, formatClockSeconds(roundDurationMs / 1000));
      } else {
        const secsLeft = targetCountdownSeconds(roundEndsAt, nowMs, options);
        urgent = secsLeft <= MATCH_TIMER_URGENCY_SECONDS;
        setTextIfChanged(targetDisplayEl, formatClockSeconds(secsLeft));
      }
    } else if (status.gameOver) {
      resetTargetCountdown();
      // A match stopped early freezes at what the clock actually read, rather
      // than dropping to 0:00 and claiming it ran out. Ceil matches the live
      // countdown's rounding, so the number does not shift as it freezes.
      const stoppedAtMs = Number(status.timeLeftAtEndMs) || 0;
      const stoppedAtSecs = stoppedAtMs > 0 ? Math.ceil(stoppedAtMs / 1000) : 0;
      urgent = stoppedAtSecs <= MATCH_TIMER_URGENCY_SECONDS;
      setTextIfChanged(targetDisplayEl, formatClockSeconds(stoppedAtSecs));
    } else {
      resetTargetCountdown();
      if (Number.isFinite(roundDurationMs) && roundDurationMs > 0) {
        setTextIfChanged(targetDisplayEl, formatClockSeconds(roundDurationMs / 1000));
      } else {
        setTextIfChanged(targetDisplayEl, Number(model.state?.target) || 10);
      }
    }
    targetDisplayEl.classList.toggle('is-urgent', urgent);
  }
}

// A team's header row: its icon, its name and its running total, with its
// players listed underneath. Cached like the player rows are, so reordering the
// list is still a matter of re-appending existing nodes.
function ensureTeamHeadRow(team) {
  const existing = ui.scoreTeamRows.get(team);
  if (existing) return existing;
  const row = document.createElement('li');
  row.className = 'score-team-head';
  row.dataset.team = team;

  const icon = document.createElement('i');
  icon.className = `bi ${teamIconClass(team)}`;
  icon.setAttribute('aria-hidden', 'true');

  const label = document.createElement('span');
  label.className = 'score-team-name';
  label.textContent = teamLabel(team);

  const total = document.createElement('span');
  total.className = 'score-team-total';

  row.append(icon, label, total);
  const refs = { row, total };
  ui.scoreTeamRows.set(team, refs);
  return refs;
}

function renderScores(nowMs = serverNowMs(), options = {}) {
  renderTargetDisplay(nowMs, options);
  if (!scoreListEl) return;

  const scores = Array.isArray(model.state?.scores) ? model.state.scores : [];
  const roundScores = Array.isArray(model.state?.roundScores) ? model.state.roundScores : [];
  const players = Array.isArray(model.state?.players) ? model.state.players : [];
  const status = model.state?.status || {};
  const showAllRoundPoints = Boolean(status.waitingNext || status.gameOver);
  const playerCount = Math.max(scores.length, players.length);
  const standings = Array.from({ length: playerCount }, (_, id) => {
    const scoreValue = Number(scores[id]);
    const roundPointsValue = Number(roundScores[id]);
    return {
      id,
      score: Number.isFinite(scoreValue) ? Math.max(0, Math.trunc(scoreValue)) : 0,
      roundPoints: Number.isFinite(roundPointsValue) ? Math.max(0, Math.trunc(roundPointsValue)) : 0
    };
  });
  // Which side each player is on, from the same player payload the arena draws.
  const teamById = new Map();
  for (const player of players) {
    const team = normalizeTeam(player?.team);
    if (team) teamById.set(Number(player.id), team);
  }
  const teamMode = Boolean(model.state?.status?.teamMode) && teamById.size > 0;
  // Who is actually here. The engine sends `connected` per player -- false for a
  // dropped socket in its grace window and, now, for anyone who left outright --
  // and it is already what greys their curve in the arena. The score panel says
  // the same thing: the row stays, because the match roster is frozen and their
  // score still counts, but it reads as somebody who is no longer playing.
  // Absent means present: a bot has no socket to lose, and a state that predates
  // the flag should not dim the whole board.
  const awayById = new Map();
  for (const player of players) {
    awayById.set(Number(player?.id), player?.connected === false);
  }
  for (const entry of standings) entry.team = teamById.get(entry.id) || null;
  standings.sort((a, b) => {
    // In team mode the list is grouped by side first: a player's rank matters
    // against their own team-mates, and the side's total is the result.
    if (teamMode && a.team !== b.team) return a.team === TEAM_FIRE ? -1 : 1;
    if (b.score !== a.score) return b.score - a.score;
    return a.id - b.id;
  });

  const scorePanel = scoreListEl.closest('.score-panel');
  scorePanel?.classList.toggle('is-large-roster', standings.length >= 7);
  scoreListEl.dataset.playerCount = String(standings.length);

  const activeIds = new Set();
  for (const entry of standings) {
    activeIds.add(entry.id);
  }
  for (const [id, refs] of ui.scoreRows) {
    if (activeIds.has(id)) continue;
    refs.row.remove();
    ui.scoreRows.delete(id);
  }

  const totals = model.state?.status?.teamScores || null;
  const nextOrderSig = `${teamMode ? 't' : 'f'}:${standings.map((entry) => `${entry.id}${entry.team || ''}`).join(',')}`;
  const expectedChildren = standings.length + (teamMode ? 2 : 0);
  if (ui.scoreOrderSig !== nextOrderSig || scoreListEl.children.length !== expectedChildren) {
    const fragment = document.createDocumentFragment();
    let openTeam = null;
    for (const entry of standings) {
      if (teamMode && entry.team && entry.team !== openTeam) {
        openTeam = entry.team;
        fragment.appendChild(ensureTeamHeadRow(entry.team).row);
      }
      fragment.appendChild(ensureScoreRow(entry.id).row);
    }
    scoreListEl.replaceChildren(fragment);
    ui.scoreOrderSig = nextOrderSig;
  }
  if (teamMode && totals) {
    // Read off the status the engine sends rather than added up here, so the
    // sidebar cannot disagree with the side it declares the winner.
    const fire = Math.max(0, Math.trunc(Number(totals.fire) || 0));
    const water = Math.max(0, Math.trunc(Number(totals.water) || 0));
    setTextIfChanged(ensureTeamHeadRow(TEAM_FIRE).total, String(fire));
    setTextIfChanged(ensureTeamHeadRow(TEAM_WATER).total, String(water));
    ensureTeamHeadRow(TEAM_FIRE).row.classList.toggle('is-leading', fire > water);
    ensureTeamHeadRow(TEAM_WATER).row.classList.toggle('is-leading', water > fire);
  }

  const leaderId = standings.length > 0 && standings[0].score > 0 ? standings[0].id : -1;
  for (const entry of standings) {
    const rowRefs = ensureScoreRow(entry.id);
    const playerColor = playerColorById(entry.id);
    if (rowRefs.dot.dataset.color !== playerColor) {
      rowRefs.dot.dataset.color = playerColor;
      rowRefs.dot.style.background = playerColor;
    }
    rowRefs.row.classList.toggle('leader', entry.id === leaderId);
    const away = awayById.get(entry.id) === true;
    rowRefs.row.classList.toggle('is-away', away);
    const network = net.playerNetworkStats.get(entry.id);
    const lagging = !status.gameOver && !away && Boolean(network?.lagging);
    rowRefs.row.classList.toggle('is-lagging', lagging);
    rowRefs.lag.hidden = !lagging;
    if (lagging) {
      const rttMs = Math.max(0, Math.round(Number(network?.rttMs) || 0));
      const jitterMs = Math.max(0, Math.round(Number(network?.jitterMs) || 0));
      const detail = rttMs > 0
        ? `${rttMs} ms ping${jitterMs > 0 ? `, ${jitterMs} ms jitter` : ''}`
        : 'connection is not responding';
      rowRefs.lag.title = `Lagging: ${detail}`;
      rowRefs.lag.setAttribute('aria-label', rowRefs.lag.title);
    } else {
      rowRefs.lag.removeAttribute('title');
      rowRefs.lag.removeAttribute('aria-label');
    }
    setTextIfChanged(rowRefs.label, playerLabelById(entry.id, { capitalizeYou: false }));
    setTextIfChanged(rowRefs.score, entry.score);

    const showRoundDelta = showAllRoundPoints;
    rowRefs.roundDelta.hidden = !showRoundDelta;
    if (showRoundDelta) {
      setTextIfChanged(rowRefs.roundDelta, `+${entry.roundPoints}`);
      if (rowRefs.roundDelta.dataset.color !== playerColor) {
        rowRefs.roundDelta.dataset.color = playerColor;
        rowRefs.roundDelta.style.backgroundColor = playerColor;
        rowRefs.roundDelta.style.borderColor = playerColor;
      }
      rowRefs.roundDelta.classList.toggle('is-zero', entry.roundPoints <= 0);
    } else {
      rowRefs.roundDelta.classList.remove('is-zero');
      rowRefs.roundDelta.textContent = '';
    }
  }
}

export {
  renderScores,
  renderTargetDisplay
};
